import { styled } from "styled-components";
import { cond, get, propEq, stubTrue } from "lodash/fp";
import { rgba, borderRadius } from "polished";
const InputBox = styled("div")({
  display: "flex"
});
const InputInner = styled("div")(
  {
    flex: "1 1 0",
    position: "relative"
  },
  ({ maxWidth }) => ({ maxWidth: maxWidth || void 0 })
);
const InputImpl = styled("input")(
  ({ theme, $magnitude }) => ({
    appearance: "textfield",
    backgroundClip: "padding-box",
    backgroundColor: "white",
    border: `1px solid ${theme.controlBorderColor}`,
    borderRadius: 2,
    boxSizing: "border-box",
    color: theme.color.default,
    fontFamily: theme.fontFamily.default,
    fontSize: {
      small: theme.fontSize.default,
      medium: theme.fontSize.default,
      large: theme.fontSize.default,
      xlarge: theme.fontSize.lg
    }[$magnitude],
    fontWeight: theme.fontWeight.normal,
    lineHeight: {
      small: theme.lineHeight.default,
      medium: theme.lineHeight.default,
      large: theme.lineHeight.default,
      xlarge: theme.lineHeight.lg
    }[$magnitude],
    margin: 0,
    outline: 0,
    width: "100%",
    transition: "0.3s",
    "&::placeholder": {
      color: theme.color.monochrome6
    },
    [`&[type=number]::-webkit-outer-spin-button,
      &[type=number]::-webkit-inner-spin-button,
      &[type="search"]::-webkit-search-cancel-button,
      &[type="search"]::-webkit-search-decoration
    `]: {
      "-webkit-appearance": "none",
      margin: 0
    }
  }),
  ({ role }) => role === "button" ? {
    cursor: "pointer"
  } : void 0,
  cond([
    [
      get("disabled"),
      ({ theme }) => ({
        backgroundColor: "rgba(0, 0, 0, 0.05)",
        color: theme.color.gray7,
        pointerEvents: "none"
      })
    ],
    [
      propEq("$tone", "critical"),
      ({ theme }) => ({
        borderColor: theme.color.red6,
        "&:focus": {
          boxShadow: `0 0 0 2px ${rgba(theme.color.red6, 0.25)}`
        }
      })
    ],
    [
      get("aria-expanded"),
      ({ theme }) => ({
        borderColor: theme.color.blue6,
        boxShadow: `0 0 0 2px ${rgba(theme.color.blue6, 0.25)}`
      })
    ],
    [
      stubTrue,
      ({ theme }) => ({
        "&:hover": {
          borderColor: theme.color.blue6
        },
        "&:focus": {
          borderColor: theme.color.blue6,
          boxShadow: `0 0 0 2px ${rgba(theme.color.blue6, 0.25)}`
        }
      })
    ]
  ]),
  ({ readOnly, role }) => readOnly && role !== "button" ? {
    backgroundColor: "rgba(0, 0, 0, 0.05)"
  } : void 0,
  ({ $cancellable, $magnitude }) => {
    const paddingValue = {
      small: 1,
      medium: 3,
      large: 5,
      xlarge: 5
    }[$magnitude];
    return {
      paddingTop: paddingValue,
      paddingRight: $cancellable ? paddingValue + 28 : 8,
      paddingBottom: paddingValue,
      paddingLeft: 8
    };
  },
  ({ $adjoin }) => ({
    both: { borderRadius: 0 },
    left: borderRadius("left", 0),
    right: borderRadius("right", 0)
  })[$adjoin],
  ({ $appearance }) => ({
    bare: {
      background: "transparent",
      borderColor: "transparent",
      boxShadow: "none",
      "&:hover, &:active, &:focus": {
        background: "transparent",
        borderColor: "transparent",
        boxShadow: "none"
      }
    }
  })[$appearance],
  ({ $textAlign }) => ({ textAlign: $textAlign })
);
const InputAuxillary = styled("div")(
  {
    position: "absolute",
    transform: "translateY(-50%)",
    top: "50%"
  },
  ({ $magnitude }) => ({
    small: { right: 2 },
    medium: { right: 4 },
    large: { right: 6 },
    xlarge: { right: 6 }
  })[$magnitude]
);
export {
  InputAuxillary,
  InputBox,
  InputImpl,
  InputInner
};
